import { inject, signal, Directive } from "@angular/core";
import { filter, distinctUntilChanged, Observable, combineLatest } from "rxjs";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import {
  AllowedMainFilterType,
  CountryFilter,
  CountryMainFilterType,
  MainFilter,
} from "@bitwarden/web-vault/app/models/types/filter.types";
import { FilterControlType } from "@bitwarden/web-vault/app/services/store/calculation-base/controls/filter.control";
import { ViewsFilterInstitutionHelper } from "@bitwarden/web-vault/app/gloss/views/views-control/views-customize/views.filter.helper.institution";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { CalculationBaseStoreService } from "@bitwarden/web-vault/app/services/store/calculation-base/calculation-base.store.service";
import { DataPluginStoreService } from "@bitwarden/web-vault/app/services/store/data-plugin/data-plugin.store.service";

@Directive()
export abstract class ViewsBaseFilter {
  protected userStore = inject(UserStoreService);
  private calculationStore: CalculationBaseStoreService;
  private dataPluginStore = inject(DataPluginStoreService);
  protected institutionFilterHelper = inject(ViewsFilterInstitutionHelper);

  private filtersSelected$: Observable<FilterControlType>;
  private filtersOptions$: Observable<FilterControlType>;

  countryViews = this.dataPluginStore.theCountryList.theCountryViews();

  options: FilterControlType;
  selected: FilterControlType;

  filter = signal<MainFilter<AllowedMainFilterType>>(null);
  filterObservable$: Observable<MainFilter<AllowedMainFilterType>> = toObservable(this.filter);

  constructor(calculationStore: CalculationBaseStoreService) {
    this.setCalculationStore(calculationStore);

    const filterSelectedChange$ = this.filtersSelected$.pipe(
      filter((x) => !!x),
      distinctUntilChanged((x, y) => JSON.stringify(x) === JSON.stringify(y)),
      takeUntilDestroyed(),
    );

    const filterOptionsChange$ = this.filtersOptions$.pipe(
      filter((x) => !!x),
      distinctUntilChanged((x, y) => JSON.stringify(x) === JSON.stringify(y)),
      takeUntilDestroyed(),
    );

    combineLatest([filterSelectedChange$, filterOptionsChange$]).subscribe(
      this.syncFromStore.bind(this),
    );
  }

  setCalculationStore(calculationStore: CalculationBaseStoreService) {
    this.calculationStore = calculationStore;
    this.filtersSelected$ = toObservable(this.calculationStore.filters.selected);
    this.filtersOptions$ = toObservable(this.calculationStore.filters.options);
    this.options = this.calculationStore.filters.options();
    this.selected = { ...this.calculationStore.filters.selected() };
  }

  resetFilter() {
    this.selected.countries = this.options.countries;
    this.selected.institutions = this.options.institutions;
    this.selected.accounts = this.options.accounts;
    this.selected.categories = this.options.categories;
    this.setFilter();
  }

  setFilter() {
    const countryMap = this.createCountryFilter();
    const filter: MainFilter<CountryMainFilterType> = {
      countryFilter: countryMap,
    };
    this.filter.set(filter);
  }

  createCountryFilter() {
    const countryMap: Map<string, CountryFilter> = new Map();
    for (const countryCode of this.options.countries) {
      const countryView = this.countryViews.find((cv) => cv.code === countryCode);
      const isSelectedCountry = this.selected.countries.includes(countryCode);
      const countryFilter: CountryFilter = {
        view: countryView,
        childFilter: this.institutionFilterHelper.getFilter(
          this.options,
          this.selected,
          countryView,
        ),
        isDisabled: false,
        isSelected: isSelectedCountry,
      };
      countryMap.set(countryCode, countryFilter);
    }
    return countryMap;
  }

  /** Drops any unconfirmed draft (Reset or dropdown picks closed without Confirm) and rebuilds the
   * filter from the applied store selection. Called each time the filter dialog opens. */
  syncFromStore() {
    this.options = this.calculationStore.filters.options();
    this.selected = { ...this.calculationStore.filters.selected() };
    this.setFilter();
  }

  onCountrySelect(selectedCountryCodes: string[]) {
    this.selected.countries = selectedCountryCodes;
    this.setFilter();
  }

  onInstitutionSelect(selectedInstitutionIds: string[]) {
    this.selected.institutions = selectedInstitutionIds;
    this.setFilter();
  }

  onAccountSelect(selectedAccountIds: string[]) {
    this.selected.accounts = selectedAccountIds;
    this.setFilter();
  }
}
